/**
 * createStore.ts
 *
 * Factory function for creating Zustand-based stores with:
 * - Command-based mutations
 * - Invariants (permissions + normalization + validation)
 * - Policies (side effects with init + onStateChange)
 * - DevTools integration
 * - React hooks for components
 *
 * Design Pattern:
 * - Zustand for state management (no Provider needed)
 * - Object mapping for command handlers (no switch statements)
 * - Functional approach (no classes)
 */

import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { Command, CommandHandlerMap, CommandMeta, CommandMetaInput, Dispatch } from './types';
import { Invariants } from './invariants';
import { Policy, PolicyContext } from './policies';
import { LogAdapter, LogEvent } from './CommandStore';

/**
 * Store configuration.
 */
export type StoreConfig<TState, TCommand extends Command> = {
  /** Store name (for DevTools) */
  name: string;

  /** Initial state */
  initialState: TState;

  /** Command handlers (object map) */
  handlers: CommandHandlerMap<TState, TCommand>;

  /** Invariants (optional) */
  invariants?: Invariants<TState, TCommand>;

  /** Policies (optional) */
  policies?: Policy<TState, TCommand>[];

  /** Log adapter for command logging (optional) */
  logAdapter?: LogAdapter;
};

/**
 * Internal store state (includes errors).
 */
type InternalStoreState<TState> = {
  state: TState;
  errors: Record<string, string> | null;
};

/**
 * Create a Zustand-based store with command-based mutations.
 *
 * @param config Store configuration
 * @returns Object with hooks and methods for store interaction
 *
 * @example
 * ```typescript
 * const {
 *   useSelector,
 *   useErrors,
 *   useDispatch,
 *   usePolicies,
 *   getState,
 *   dispatch,
 * } = createStore({
 *   name: 'wizard',
 *   initialState: { activeStep: 'step1', data: {} },
 *   handlers: wizardHandlers,
 *   invariants: wizardInvariants,
 *   policies: wizardPolicies,
 * });
 * ```
 */
export function createStore<TState, TCommand extends Command>(
  config: StoreConfig<TState, TCommand>
) {
  // Validate initial state
  let validatedInitialState = config.initialState;
  if ( config.invariants?.normalize ) {
    validatedInitialState = config.invariants.normalize( validatedInitialState );
  }
  const initialErrors = config.invariants?.validate
    ? config.invariants.validate( validatedInitialState )
    : null;

  // Create Zustand store with DevTools and selector subscriptions
  const useStore = create<InternalStoreState<TState>>()(
    devtools(
      subscribeWithSelector(() => ({
        state: validatedInitialState,
        errors: initialErrors
      })),
      { name: config.name }
    )
  );

  /**
   * Helper function to log command dispatch events.
   */
  const logCommand = (
    command: TCommand,
    meta: import( './types' ).CommandMeta,
    result: LogEvent['result']
  ): void => {
    if ( !config.logAdapter ) return;

    const event: LogEvent = {
      storeName: config.name,
      command,
      meta,
      result,
      loggedAt: Date.now()
    };

    try {
      config.logAdapter.log( event );
    } catch ( error ) {
      console.error( `[${config.name}] LogAdapter error:`, error );
    }
  };

  /**
   * Dispatch function - applies commands with invariants.
   *
   * Flow:
   * 1. Check permissions (canApply)
   * 2. Apply command via handler
   * 3. Normalize state
   * 4. Validate state
   * 5. Update store
   * 6. Log command (if logAdapter provided)
   */
  const dispatch: Dispatch<TCommand> = ( command, metaInput ) => {
    // Auto-populate timestamp if not provided
    const meta: CommandMeta = {
      ...metaInput,
      timestamp: metaInput.timestamp ?? Date.now()
    };

    const { state } = useStore.getState();
    let result: LogEvent['result'] = 'success';

    // 1. Check permissions
    if ( config.invariants?.canApply ) {
      const allowed = config.invariants.canApply({ state, command, meta });

      if ( !allowed ) {
        console.warn(
          `[${config.name}] Command blocked by permissions:`,
          command.type,
          'from source:',
          meta.source
        );
        result = 'rejected';
        logCommand( command, meta, result );
        return;
      }
    }

    // 2. Get handler
    const handler = config.handlers[command.type as keyof typeof config.handlers];
    if ( !handler ) {
      console.error(
        `[${config.name}] No handler found for command:`,
        command.type
      );
      result = 'handler_not_found';
      logCommand( command, meta, result );
      return;
    }

    // 3. Apply command
    let newState = handler( state, command as any, meta );

    // 4. Normalize
    if ( config.invariants?.normalize ) {
      newState = config.invariants.normalize( newState );
    }

    // 5. Validate
    const errors = config.invariants?.validate
      ? config.invariants.validate( newState )
      : null;

    // 6. Update store
    useStore.setState(
      { state: newState, errors },
      false, // Don't replace, merge
      `${command.type}` // Action name for DevTools
    );

    // 7. Log successful dispatch
    logCommand( command, meta, result );
  };

  /**
   * Hook to initialize policies (runs on mount).
   *
   * Lifecycle:
   * 1. Calls init() for each policy
   * 2. Subscribes to state changes
   * 3. Calls onStateChange() when shouldReact() returns true
   * 4. Cleanup on unmount
   *
   * @param policies Array of policies to activate
   *
   * @example
   * ```typescript
   * function WizardComponent() {
   *   usePolicies([urlHydratePolicy, urlSyncPolicy, apiPolicy]);
   *   // ... component code
   * }
   * ```
   */
  function usePolicies( policies: Policy<TState, TCommand>[] = []) {
    // Use ref to store policies for stable subscription callback
    const policiesRef = useRef( policies );
    policiesRef.current = policies;

    // Track if already initialized to prevent double-init in StrictMode
    const initializedRef = useRef( false );

    // Create policy context (stable reference)
    const ctx: PolicyContext<TState, TCommand> = {
      getState: () => useStore.getState().state,
      dispatch
    };

    // Initialize policies and subscribe to state changes in a single effect
    // This ensures subscription is active when init() dispatches commands
    useEffect(() => {
      // Guard against double-init in React StrictMode
      if ( initializedRef.current ) {
        return;
      }
      initializedRef.current = true;

      const cleanups: (() => void )[] = [];

      // 1. Set up subscription FIRST (before init)
      // Use policiesRef.current so we always have latest policies
      const unsubscribe = useStore.subscribe(
        ( internalState ) => internalState.state,
        ( next, prev ) => {
          for ( const policy of policiesRef.current ) {
            if ( policy.onStateChange && policy.shouldReact?.( prev, next )) {
              policy.onStateChange({ prev, next, ctx });
            }
          }
        }
      );
      cleanups.push( unsubscribe );

      // 2. Initialize policies AFTER subscription is active
      for ( const policy of policiesRef.current ) {
        if ( policy.init ) {
          console.log( `[${config.name}] Initializing policy: ${policy.name}` );
          const cleanup = policy.init( ctx );
          if ( cleanup ) {
            cleanups.push( cleanup );
          }
        }
      }

      // 3. Add policy-specific cleanup functions
      for ( const policy of policiesRef.current ) {
        if ( policy.cleanup ) {
          cleanups.push( policy.cleanup );
        }
      }

      // Cleanup on unmount
      return () => {
        cleanups.forEach(( cleanup ) => cleanup());
        initializedRef.current = false;
      };
    }, []);
  }

  // Return public API
  return {
    /**
     * Hook to select a slice of state.
     * Only re-renders when the selected value changes.
     *
     * @param selector Function to extract value from state
     * @returns Selected value
     *
     * @example
     * ```typescript
     * const country = useSelector(s => s.data.country);
     * const activeStep = useSelector(s => s.activeStep);
     * ```
     */
    useSelector: <TSelected>( selector: ( state: TState ) => TSelected ): TSelected =>
      useStore(( s ) => selector( s.state )),

    /**
     * Hook to get validation errors.
     * Re-renders when errors change.
     *
     * @returns Current validation errors or null
     *
     * @example
     * ```typescript
     * const errors = useErrors();
     * if (errors) {
     *   return <Alert>{errors.country}</Alert>;
     * }
     * ```
     */
    useErrors: (): Record<string, string> | null =>
      useStore(( s ) => s.errors ),

    /**
     * Hook to get the dispatch function.
     * Never re-renders (dispatch is stable).
     *
     * @returns Dispatch function
     *
     * @example
     * ```typescript
     * const dispatch = useDispatch();
     * dispatch(
     *   { type: 'wizard/data/setCountry', payload: { country: 'AR' } },
     *   { source: 'user', timestamp: Date.now() }
     * );
     * ```
     */
    useDispatch: () => dispatch,

    /**
     * Hook to initialize policies.
     * Call this in your component to activate policies.
     *
     * @param policies Array of policies to activate
     *
     * @example
     * ```typescript
     * function WizardComponent() {
     *   usePolicies([urlHydratePolicy, urlSyncPolicy, apiPolicy]);
     *   // ... rest of component
     * }
     * ```
     */
    usePolicies,

    /**
     * Get current state (for external use, like AI coordinator).
     *
     * @returns Current state
     *
     * @example
     * ```typescript
     * const currentState = getState();
     * console.log(currentState.activeStep);
     * ```
     */
    getState: () => useStore.getState().state,

    /**
     * Dispatch commands (for external use, like AI coordinator).
     *
     * @example
     * ```typescript
     * dispatch(
     *   { type: 'wizard/navigation/goToStep', payload: { stepId: 'step2' } },
     *   { source: 'ai', timestamp: Date.now() }
     * );
     * ```
     */
    dispatch
  };
}
